import React from "react";
import { motion } from "framer-motion";
import {
  Quote,
  Star,
  CheckCircle2,
  ShieldCheck,
  Clock,
} from "lucide-react";
import testimonialsData from "../data/testimonialsData";

/**
 * Helper to compute clean initials from names
 * Examples: "Dr. Vijay Sharma" -> "VS", "Hemant Marothia" -> "HM"
 */
const getInitials = (name) => {
  if (!name) return "CC";
  const clean = name.replace(/^(Dr\.|Mr\.|Ms\.|Mrs\.)\s+/i, "").trim();
  const parts = clean.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Reusable Testimonial Card Component
 * Supports:
 * - Client Name
 * - Company Name (kept separate)
 * - Actual testimonial text (when provided)
 * - Optional rating (1-5 stars)
 * - Initials-based avatar
 */
export const TestimonialCard = ({ testimonial }) => {
  const { clientName, company, content, rating } = testimonial;
  const initials = getInitials(clientName);
  const hasContent = Boolean(content && content.trim());

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -4 }}
      className="group relative rounded-2xl p-6 sm:p-7 border border-white/10 hover:border-[#7C4DFF]/50 transition-all duration-300 flex flex-col justify-between shadow-lg hover:shadow-[0_12px_35px_rgba(124,77,255,0.2)] overflow-hidden"
      style={{
        background: "linear-gradient(145deg, rgba(15, 23, 48, 0.85) 0%, rgba(9, 14, 32, 0.95) 100%)",
      }}
    >
      {/* Decorative ambient corner glow */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-[radial-gradient(circle_at_top_right,rgba(124,77,255,0.15),transparent_70%)] pointer-events-none" />

      <div>
        {/* Top Header: Initials Avatar & Verification Badge */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3.5">
            {/* Initials-based Avatar */}
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm tracking-wider text-white shrink-0 shadow-md border border-white/20 transition-transform duration-300 group-hover:scale-105"
              style={{
                background: "linear-gradient(135deg, #7C4DFF, #27B8FF)",
              }}
              aria-label={`Avatar for ${clientName}`}
            >
              {initials}
            </div>

            <div>
              <h3
                className="text-base sm:text-lg font-bold text-white leading-tight group-hover:text-ai-glow transition-colors"
                style={{ fontFamily: '"Space Grotesk", sans-serif' }}
              >
                {clientName}
              </h3>
              <p className="text-xs sm:text-sm font-semibold text-[#27B8FF] mt-0.5">
                {company}
              </p>
            </div>
          </div>

          <Quote className="w-6 h-6 text-[#7C4DFF]/40 group-hover:text-[#7C4DFF] transition-colors shrink-0" />
        </div>

        {/* Content Body */}
        {hasContent ? (
          <div>
            {rating && (
              <div className="flex items-center gap-1 mb-3">
                {[...Array(rating)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-[#F59E0B] text-[#F59E0B]" />
                ))}
              </div>
            )}
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed italic mb-6">
              "{content}"
            </p>
          </div>
        ) : (
          /* Visually polished authentic status without fabricated quotes */
          <div className="py-2 mb-6">
            <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
              Partnered with CLIENTCODE AI on enterprise technology and software initiatives.
              Official case evaluation and formal client review are currently under preparation.
            </p>
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="pt-4 border-t border-white/5 flex items-center justify-between text-xs text-slate-400">
        <span className="inline-flex items-center gap-1.5 text-[#27B8FF]">
          <CheckCircle2 className="w-3.5 h-3.5 text-[#27B8FF]" />
          Verified Client Partner
        </span>

        {hasContent ? (
          <span className="inline-flex items-center gap-1 text-slate-400">
            <ShieldCheck className="w-3.5 h-3.5 text-[#7C4DFF]" />
            Verified Review
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-slate-400">
            <Clock className="w-3.5 h-3.5 text-[#7C4DFF]" />
            Review in Progress
          </span>
        )}
      </div>
    </motion.div>
  );
};

const ClientTestimonials = () => {
  return (
    <section
      id="testimonials"
      className="section-padding relative overflow-hidden"
      style={{ background: "#060B1F" }}
    >
      {/* Background Decorative Tech Elements */}
      <div className="absolute inset-0 tech-grid opacity-10 pointer-events-none" />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[380px] rounded-full opacity-[0.08] blur-[140px] pointer-events-none"
        style={{ background: "radial-gradient(circle, #7C4DFF, #27B8FF)" }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Centered Heading & Subtitle */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-10"
        >
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: '"Space Grotesk", sans-serif' }}
          >
            What Our <span className="gradient-text">Clients Say</span>
          </h2>

          <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
            Trusted partnerships. Meaningful results.
          </p>
        </motion.div>

        {/* Responsive Grid of Glassmorphism Testimonial Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
          {testimonialsData.map((testimonial) => (
            <TestimonialCard key={testimonial.id} testimonial={testimonial} />
          ))}
        </div>
      </div>

      {/* Section Divider connecting to Affiliation & Accreditation */}
      <div className="section-divider mt-6 sm:mt-8" />
    </section>
  );
};

export default React.memo(ClientTestimonials);

